// src/start/events.ts: homebuild's own funnel measurement from the browser (docs/onsite/ONSITE.md 9.1). Enum values
// only, never a name, an email or free text; a random visit id per tab, no cookies. Events are batched and sent to
// POST /api/e; a failure is dropped (measurement never gets in a visitor's way).
import type { FunnelEventInput, FunnelEventName } from "../../shared/api";
import { session } from "../lib/storage";
import { newVisitId } from "./model";

/** The form variant on every event (open question 3: contact last is the only variant today). */
export const VARIANT = "contact-last";
const VISIT_KEY = "start:visit";
const MAX_BATCH = 12;

let queue: FunnelEventInput[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let listening = false;

export function visitId(): string {
  let v = session.get(VISIT_KEY);
  if (!v || !/^[A-Za-z0-9_-]{8,40}$/.test(v)) {
    v = newVisitId();
    session.set(VISIT_KEY, v);
  }
  return v;
}

function body(events: FunnelEventInput[]): string {
  return JSON.stringify({ visitId: visitId(), events });
}

/** Sends what is queued now. `beacon` on page hide, so the last events survive a navigation away. */
export function flushEvents(beacon = false): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  while (queue.length) {
    const batch = queue.slice(0, MAX_BATCH);
    queue = queue.slice(MAX_BATCH);
    const data = body(batch);
    try {
      if (beacon && typeof navigator.sendBeacon === "function") {
        navigator.sendBeacon("/api/e", new Blob([data], { type: "text/plain" }));
        continue;
      }
      void fetch("/api/e", { method: "POST", headers: { "content-type": "text/plain" }, body: data, keepalive: true, credentials: "same-origin" }).catch(() => undefined);
    } catch {
      /* measurement is best effort */
    }
  }
}

/**
 * Queues one event; they go out together a moment later. `variant` is the form's; events that belong to no form
 * variant (the installed app's, src/pwa/prompt.ts) pass null.
 */
export function track(event: FunnelEventName, fields: Omit<FunnelEventInput, "event" | "variant"> = {}, variant: string | null = VARIANT): void {
  if (typeof window === "undefined") return;
  queue.push({ event, ...(variant ? { variant } : {}), ...fields });
  if (!listening) {
    listening = true;
    window.addEventListener("pagehide", () => flushEvents(true));
  }
  if (queue.length >= MAX_BATCH) flushEvents();
  else if (!timer) timer = setTimeout(() => flushEvents(), 1500);
}
